import { Err, Ok, Result } from 'ts-res';
import {
  Conversion,
  KameleoonData,
  KameleoonDataType,
  PageView,
} from 'src/kameleoonData';
import { UrlEventType, UrlParameter } from 'src/requester';
import { IExternalPRNG, Utilities } from 'src/utilities';
import {
  AddDataParametersType,
  AddVisitorTrackingLineParametersType,
  GetTrackingBodyResultType,
  VisitorTrackingDataType,
} from './types';
import { MAX_BODY_SIZE, SYMBOL_SIZE } from './constants';
import { BaseVariationDataType } from 'src/variationConfiguration/types';
import { KameleoonLogger } from 'src/logging';
import { StaticData } from 'src/kameleoonData/staticData';

interface IBodyProvider {
  getTrackingBody: (visitorCode: string) => GetTrackingBodyResultType;
  addData: ({
    visitorCode,
    variationData,
    visitorData,
    userAgent,
    isConsentProvided,
    isMappingIdentifier,
  }: AddDataParametersType) => Result<void, void>;
}

export class BodyProvider implements IBodyProvider {
  private prng: IExternalPRNG;
  private currentSize = 0;
  private resultBody: string[] = [];
  private hasUserAgent = false;
  private trackingVisitors: {
    [visitorCode: string]: VisitorTrackingDataType;
  } = {};

  constructor(prng: IExternalPRNG) {
    this.prng = prng;
  }

  public getTrackingBody(): GetTrackingBodyResultType {
    const result = {
      body: this.resultBody.join('\n'),
      hasUserAgent: this.hasUserAgent,
    };

    this.cleanup();

    return result;
  }

  public addData({
    visitorCode,
    variationData,
    visitorData,
    userAgent,
    isConsentProvided,
    isMappingIdentifier,
  }: AddDataParametersType): Result<void, void> {
    const lines: string[] = [];

    const variationDataLines = this.getVariationDataLines(variationData);
    const visitorDataLines = this.getVisitorDataLines(visitorData);

    lines.push(...variationDataLines, ...visitorDataLines);

    return this.addVisitorTrackingLines({
      visitorCode,
      lines,
      userAgent,
      isConsentProvided,
      isMappingIdentifier,
    });
  }

  private getVisitorDataLines(
    visitorData: (KameleoonDataType | StaticData)[],
  ): string[] {
    const lines: string[] = [];

    for (const data of visitorData) {
      let nonce;

      if (
        !(data instanceof StaticData) &&
        (data.data.type === KameleoonData.Conversion ||
          data.data.type === KameleoonData.PageView)
      ) {
        const nonceValue = (data as Conversion | PageView).data.nonce;

        if (nonceValue) {
          nonce = nonceValue;
        } else {
          nonce = Utilities.getNonce(this.prng);
          (data as Conversion | PageView).data.nonce = nonce;
        }
      }

      if (!nonce) {
        nonce = Utilities.getNonce(this.prng);
      }

      lines.push(data.url + UrlParameter.Nonce + nonce);
    }

    return lines;
  }

  private getVariationDataLines(
    variationData: BaseVariationDataType[],
  ): string[] {
    const lines: string[] = [];

    variationData.forEach((item) => {
      lines.push(
        UrlEventType.Experiment +
          UrlParameter.Id +
          item.experimentId +
          UrlParameter.VariationId +
          item.variationId +
          UrlParameter.Nonce +
          Utilities.getNonce(this.prng),
      );
    });

    return lines;
  }

  private prepareVisitorBody(visitorCode: string): Result<void, void> {
    const visitorData = this.trackingVisitors[visitorCode];

    if (!visitorData) {
      return Err();
    }

    const { lines, userAgent, isMappingIdentifier, isConsentProvided } =
      visitorData;

    const identifier = this.getIdentifier(visitorCode, isMappingIdentifier);

    if (lines.length) {
      lines.forEach((value, index) => {
        lines[index] = value + identifier;
      });
      KameleoonLogger.debug`Sending tracking request for unsent data ${lines} of visitor ${visitorCode} with given (or not required) consent ${isConsentProvided}`;
    } else if (isConsentProvided) {
      const activityLine = this.getActivityLine();

      lines.push(activityLine + identifier);
      KameleoonLogger.debug`Sending tracking request for activity of visitor ${visitorCode} with given consent`;
    } else {
      KameleoonLogger.debug`No data to send for visitor ${visitorCode} with no consent`;
    }

    if (userAgent && lines.length) {
      lines[0] += UrlParameter.UserAgent + userAgent;
      this.hasUserAgent = true;
    }

    // --- Note ---
    // Improve after `ts-res` chaining update
    const result = this.checkSize(lines);

    if (result.ok) {
      this.resultBody.push(...lines);
    }

    return result;
  }

  private cleanup(): void {
    this.currentSize = 0;
    this.resultBody = [];
    this.hasUserAgent = false;
    this.trackingVisitors = {};
  }

  private checkSize(lines: string[]): Result<void, void> {
    let size = 0;
    size += lines.reduce((acc, line) => acc + line.length * SYMBOL_SIZE, 0);

    if (size + this.currentSize > MAX_BODY_SIZE) {
      return Err();
    }

    this.currentSize += size;

    return Ok();
  }

  private addVisitorTrackingLines({
    visitorCode,
    lines,
    userAgent,
    isConsentProvided,
    isMappingIdentifier,
  }: AddVisitorTrackingLineParametersType): Result<void, void> {
    if (!this.trackingVisitors[visitorCode]) {
      this.trackingVisitors[visitorCode] = {
        isMappingIdentifier,
        isConsentProvided,
        userAgent,
        lines,
      };
    } else {
      if (userAgent) {
        this.trackingVisitors[visitorCode].userAgent = userAgent;
      }

      this.trackingVisitors[visitorCode].lines.push(...lines);
    }

    return this.prepareVisitorBody(visitorCode);
  }

  private getActivityLine(): string {
    return (
      UrlEventType.Activity + UrlParameter.Nonce + Utilities.getNonce(this.prng)
    );
  }

  private getIdentifier(
    visitorCode: string,
    isMappingIdentifier: boolean,
  ): string {
    const identifier = isMappingIdentifier
      ? UrlParameter.MappingValue
      : UrlParameter.VisitorCode;

    return identifier + visitorCode;
  }
}
